import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft, Ship, Anchor, Sun, Moon, MapPin, ChevronRight, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getVesselClass(length) {
  if (length < 16) return 'A';
  if (length < 26) return '1';
  if (length < 40) return '2';
  return '3';
}

function getClassDescription(vesselClass) {
  const descriptions = {
    'A': 'Less than 16 feet',
    '1': '16 to 26 feet',
    '2': '26 to 40 feet',
    '3': '40 to 65 feet'
  };
  return descriptions[vesselClass] || '';
}

export default function VesselSetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vesselId = searchParams.get('vessel');
  
  const [step, setStep] = useState(1);
  const [vessels, setVessels] = useState([]);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Vessel form state
  const [vesselName, setVesselName] = useState('');
  const [vesselLength, setVesselLength] = useState('');
  const [engineType, setEngineType] = useState('');
  const [hasEnclosed, setHasEnclosed] = useState(false);

  // Trip config state
  const [location, setLocation] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('');
  const [passengerCount, setPassengerCount] = useState('1');

  const vesselClass = vesselLength ? getVesselClass(parseFloat(vesselLength)) : '';

  useEffect(() => {
    fetchVessels();
  }, []);

  useEffect(() => {
    if (vesselId && vessels.length > 0) {
      const vessel = vessels.find(v => v.vessel_id === vesselId);
      if (vessel) {
        setSelectedVessel(vessel);
        setStep(2);
      }
    }
  }, [vesselId, vessels]);

  const fetchVessels = async () => {
    try {
      const response = await axios.get(`${API}/vessels`, { withCredentials: true });
      setVessels(response.data);
    } catch (err) {
      console.error('Failed to fetch vessels:', err);
    }
  };

  const handleCreateVessel = async () => {
    if (!vesselName || !vesselLength || !engineType) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/vessels`, {
        name: vesselName,
        length: parseFloat(vesselLength),
        engine_type: engineType,
        has_enclosed_compartments: hasEnclosed
      }, { withCredentials: true });

      setSelectedVessel(response.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create vessel');
    } finally {
      setLoading(false);
    }
  };

  const handleStartChecklist = async () => {
    if (!location || !timeOfDay) {
      setError('Please select location and time');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/checklists`, {
        vessel_id: selectedVessel.vessel_id,
        location,
        time_of_day: timeOfDay,
        passenger_count: parseInt(passengerCount)
      }, { withCredentials: true });

      navigate(`/checklist/${response.data.checklist_id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start checklist');
    } finally {
      setLoading(false);
    }
  };

  const progress = step === 1 ? 33 : step === 2 ? 66 : 100;

  return (
    <div className="min-h-screen bg-[#F0F4F8] page-enter">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b-2 border-[#E2E8F0] px-6 py-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="icon"
              data-testid="back-btn"
              onClick={() => step === 1 ? navigate('/dashboard') : setStep(step - 1)}
              className="text-[#2B3A67]"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-bold text-[#2B3A67] font-['Outfit']">
              {step === 1 ? 'Vessel Setup' : 'Trip Configuration'}
            </h1>
            <div className="w-10"></div>
          </div>
          <Progress value={progress} className="h-3 bg-[#E2E8F0]" />
        </div>
      </header>

      <main className="px-6 py-6 max-w-lg mx-auto">
        {error && (
          <div className="flex items-center gap-2 p-4 mb-6 bg-[#FAEAEA] text-[#D32F2F] rounded-xl">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6">
            {/* Select Existing Vessel */}
            {vessels.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-lg font-bold text-[#2B3A67] font-['Outfit']">Select a Vessel</h2>
                <div className="space-y-3">
                  {vessels.map((vessel) => (
                    <Card
                      key={vessel.vessel_id}
                      data-testid={`select-vessel-${vessel.vessel_id}`}
                      className={`border-2 cursor-pointer transition-all ${
                        selectedVessel?.vessel_id === vessel.vessel_id
                          ? 'border-[#2B3A67] bg-[#F0F4F8]'
                          : 'border-[#E2E8F0] hover:border-[#2B3A67]'
                      }`}
                      onClick={() => {
                        setSelectedVessel(vessel);
                        setStep(2);
                      }}
                    >
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-[#2B3A67] rounded-xl flex items-center justify-center">
                            <Ship className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="font-bold text-[#2B3A67]">{vessel.name}</p>
                            <p className="text-sm text-[#4A5568]">
                              Class {vessel.vessel_class} • {vessel.length}ft • {vessel.engine_type}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-[#4A5568]" />
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[#E2E8F0]"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-[#F0F4F8] text-[#4A5568] font-medium">or add new</span>
                  </div>
                </div>
              </div>
            )}

            {/* Add New Vessel Form */}
            <Card className="border-2 border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-[#2B3A67] font-['Outfit']">
                  {vessels.length > 0 ? 'Add New Vessel' : 'Vessel Information'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#2B3A67] uppercase tracking-wide">
                    Vessel Name
                  </Label>
                  <Input
                    data-testid="vessel-name-input"
                    value={vesselName}
                    onChange={(e) => setVesselName(e.target.value)}
                    placeholder="My Boat"
                    className="h-14 text-lg border-2 border-[#E2E8F0] rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#2B3A67] uppercase tracking-wide">
                    Vessel Length (feet)
                  </Label>
                  <Input
                    data-testid="vessel-length-input"
                    type="number"
                    value={vesselLength}
                    onChange={(e) => setVesselLength(e.target.value)}
                    placeholder="22"
                    className="h-14 text-lg border-2 border-[#E2E8F0] rounded-xl"
                  />
                  {vesselClass && (
                    <div className="flex items-center gap-2 p-3 bg-[#E8F6ED] rounded-xl">
                      <Anchor className="w-5 h-5 text-[#3CB371]" />
                      <span className="font-medium text-[#2A8A53]">
                        Class {vesselClass} - {getClassDescription(vesselClass)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#2B3A67] uppercase tracking-wide">
                    Engine Type
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      data-testid="engine-outboard-btn"
                      onClick={() => setEngineType('outboard')}
                      className={`h-14 rounded-xl font-bold text-sm transition-all ${
                        engineType === 'outboard'
                          ? 'bg-[#2B3A67] text-white'
                          : 'bg-white border-2 border-[#E2E8F0] text-[#2B3A67] hover:border-[#2B3A67]'
                      }`}
                    >
                      Outboard
                    </button>
                    <button
                      type="button"
                      data-testid="engine-inboard-btn"
                      onClick={() => setEngineType('inboard')}
                      className={`h-14 rounded-xl font-bold text-sm transition-all ${
                        engineType === 'inboard'
                          ? 'bg-[#2B3A67] text-white'
                          : 'bg-white border-2 border-[#E2E8F0] text-[#2B3A67] hover:border-[#2B3A67]'
                      }`}
                    >
                      Inboard
                    </button>
                    <button
                      type="button"
                      data-testid="engine-pwc-btn"
                      onClick={() => setEngineType('pwc')}
                      className={`h-14 rounded-xl font-bold text-sm transition-all ${
                        engineType === 'pwc'
                          ? 'bg-[#2B3A67] text-white'
                          : 'bg-white border-2 border-[#E2E8F0] text-[#2B3A67] hover:border-[#2B3A67]'
                      }`}
                    >
                      PWC
                    </button>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-4 bg-[#F0F4F8] rounded-xl">
                  <Checkbox
                    id="enclosed"
                    data-testid="enclosed-checkbox"
                    checked={hasEnclosed}
                    onCheckedChange={setHasEnclosed}
                    className="w-6 h-6"
                  />
                  <Label htmlFor="enclosed" className="text-[#2B3A67] font-medium cursor-pointer">
                    Has enclosed compartments (fuel/engine)
                  </Label>
                </div>

                <Button
                  data-testid="create-vessel-btn"
                  onClick={handleCreateVessel}
                  disabled={loading || !vesselName || !vesselLength || !engineType}
                  className="w-full h-14 bg-[#2B3A67] hover:bg-[#1A264A] text-white font-bold text-lg rounded-xl"
                >
                  {loading ? 'Creating...' : 'Create Vessel & Continue'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {step === 2 && selectedVessel && (
          <div className="space-y-6">
            {/* Selected Vessel Info */}
            <Card className="border-2 border-[#3CB371] bg-[#E8F6ED]">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-[#3CB371] rounded-xl flex items-center justify-center">
                  <Ship className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-[#2A8A53]">{selectedVessel.name}</p>
                  <p className="text-sm text-[#2A8A53]">
                    Class {selectedVessel.vessel_class} • {selectedVessel.length}ft • {selectedVessel.engine_type}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Trip Configuration */}
            <Card className="border-2 border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-[#2B3A67] font-['Outfit']">
                  Trip Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#2B3A67] uppercase tracking-wide">
                    Location
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      data-testid="location-inland-btn"
                      onClick={() => setLocation('inland')}
                      className={`h-14 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                        location === 'inland'
                          ? 'bg-[#2B3A67] text-white'
                          : 'bg-white border-2 border-[#E2E8F0] text-[#2B3A67] hover:border-[#2B3A67]'
                      }`}
                    >
                      <MapPin className="w-5 h-5" />
                      Inland
                    </button>
                    <button
                      data-testid="location-coastal-btn"
                      onClick={() => setLocation('coastal')}
                      className={`h-14 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                        location === 'coastal'
                          ? 'bg-[#2B3A67] text-white'
                          : 'bg-white border-2 border-[#E2E8F0] text-[#2B3A67] hover:border-[#2B3A67]'
                      }`}
                    >
                      <MapPin className="w-5 h-5" />
                      Coastal
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#2B3A67] uppercase tracking-wide">
                    Time of Day
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      data-testid="time-day-btn"
                      onClick={() => setTimeOfDay('day')}
                      className={`h-14 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                        timeOfDay === 'day'
                          ? 'bg-[#2B3A67] text-white'
                          : 'bg-white border-2 border-[#E2E8F0] text-[#2B3A67] hover:border-[#2B3A67]'
                      }`}
                    >
                      <Sun className="w-5 h-5" />
                      Day
                    </button>
                    <button
                      data-testid="time-night-btn"
                      onClick={() => setTimeOfDay('night')}
                      className={`h-14 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                        timeOfDay === 'night'
                          ? 'bg-[#2B3A67] text-white'
                          : 'bg-white border-2 border-[#E2E8F0] text-[#2B3A67] hover:border-[#2B3A67]'
                      }`}
                    >
                      <Moon className="w-5 h-5" />
                      Night
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-bold text-[#2B3A67] uppercase tracking-wide">
                    Number of Passengers
                  </Label>
                  <Input
                    data-testid="passenger-count-input"
                    type="number"
                    min="1"
                    value={passengerCount}
                    onChange={(e) => setPassengerCount(e.target.value)}
                    className="h-14 text-lg border-2 border-[#E2E8F0] rounded-xl"
                  />
                </div>

                <Button
                  data-testid="start-checklist-btn"
                  onClick={handleStartChecklist}
                  disabled={loading || !location || !timeOfDay}
                  className="w-full h-14 bg-[#3CB371] hover:bg-[#2A8A53] text-white font-bold text-lg rounded-xl"
                >
                  {loading ? 'Starting...' : 'Start Safety Check'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
